import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, KeyRoundIcon, LockKeyholeIcon, LockKeyholeOpenIcon, PencilIcon, PlusIcon, ShieldCheckIcon, UserRoundIcon } from 'lucide-react';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Modal } from './Modal';
import { TextInput } from './TextInput';
type UserStatus = 'Aktiv' | 'Gesperrt';
type ManagedUser = {
  id: string;
  name: string;
  status: UserStatus;
  hasOfficeAccess: boolean;
  hasProductionAccess: boolean;
  isSupervisor: boolean;
  supervisorCode: string;
};
type UserFormValues = {
  name: string;
  password: string;
  hasOfficeAccess: boolean;
  hasProductionAccess: boolean;
  isSupervisor: boolean;
  supervisorCode: string;
};
type UserFormErrors = {
  name?: string;
  password?: string;
  permissions?: string;
  supervisorCode?: string;
};
const initialUsers: ManagedUser[] = [{
  id: 'usr-001',
  name: 'L. Müller',
  status: 'Aktiv',
  hasOfficeAccess: true,
  hasProductionAccess: true,
  isSupervisor: true,
  supervisorCode: 'SV-2847'
}, {
  id: 'usr-002',
  name: 'F. Meyer',
  status: 'Aktiv',
  hasOfficeAccess: false,
  hasProductionAccess: true,
  isSupervisor: false,
  supervisorCode: ''
}, {
  id: 'usr-003',
  name: 'M. Schmidt',
  status: 'Aktiv',
  hasOfficeAccess: true,
  hasProductionAccess: true,
  isSupervisor: true,
  supervisorCode: 'SV-1739'
}, {
  id: 'usr-004',
  name: 'A. Köhler',
  status: 'Gesperrt',
  hasOfficeAccess: true,
  hasProductionAccess: false,
  isSupervisor: false,
  supervisorCode: ''
}];
const emptyFormValues: UserFormValues = {
  name: '',
  password: '',
  hasOfficeAccess: false,
  hasProductionAccess: false,
  isSupervisor: false,
  supervisorCode: ''
};
export function UserManagement() {
  const [users, setUsers] = useState<ManagedUser[]>(initialUsers);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [formValues, setFormValues] = useState<UserFormValues>(emptyFormValues);
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const isEditing = editingUser !== null;
  const dialogTitle = isEditing ? 'Benutzer bearbeiten' : 'Benutzer anlegen';
  const dialogDescription = isEditing ? 'Zugangsdaten und Berechtigungen dieses Benutzerkontos aktualisieren.' : 'Zugangsdaten und Berechtigungen für ein neues Benutzerkonto festlegen.';
  useEffect(() => {
    const loadingTimeoutId = window.setTimeout(() => setIsLoading(false), 2000);
    return () => window.clearTimeout(loadingTimeoutId);
  }, []);
  function openCreateDialog() {
    setEditingUser(null);
    setFormValues(emptyFormValues);
    setErrors({});
    setIsDialogOpen(true);
  }
  function openEditDialog(user: ManagedUser) {
    setEditingUser(user);
    setFormValues({
      name: user.name,
      password: '',
      hasOfficeAccess: user.hasOfficeAccess,
      hasProductionAccess: user.hasProductionAccess,
      isSupervisor: user.isSupervisor,
      supervisorCode: user.supervisorCode
    });
    setErrors({});
    setIsDialogOpen(true);
  }
  function handleDialogOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      setErrors({});
    }
  }
  function toggleUserStatus(user: ManagedUser) {
    const nextStatus: UserStatus = user.status === 'Aktiv' ? 'Gesperrt' : 'Aktiv';
    setUsers(currentUsers => currentUsers.map(currentUser => currentUser.id === user.id ? {
      ...currentUser,
      status: nextStatus
    } : currentUser));
    setEditingUser(currentUser => currentUser?.id === user.id ? {
      ...currentUser,
      status: nextStatus
    } : currentUser);
    setSuccessMessage(nextStatus === 'Gesperrt' ? `${user.name} wurde gesperrt.` : `${user.name} wurde entsperrt.`);
    window.setTimeout(() => setSuccessMessage(''), 4000);
  }
  function updateFormValue<Key extends keyof UserFormValues>(key: Key, value: UserFormValues[Key]) {
    setFormValues(current => ({
      ...current,
      [key]: value,
      ...(key === 'isSupervisor' && !value ? {
        supervisorCode: ''
      } : {})
    }));
    setErrors(current => ({
      ...current,
      [key]: undefined,
      ...(key === 'hasOfficeAccess' || key === 'hasProductionAccess' ? {
        permissions: undefined
      } : {})
    }));
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateUserForm(formValues, isEditing);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    const normalizedName = formValues.name.trim();
    if (editingUser) {
      setUsers(currentUsers => currentUsers.map(user => user.id === editingUser.id ? {
        ...user,
        name: normalizedName,
        hasOfficeAccess: formValues.hasOfficeAccess,
        hasProductionAccess: formValues.hasProductionAccess,
        isSupervisor: formValues.isSupervisor,
        supervisorCode: formValues.isSupervisor ? formValues.supervisorCode.trim().toUpperCase() : ''
      } : user));
      setSuccessMessage(`${normalizedName} wurde aktualisiert.`);
    } else {
      setUsers(currentUsers => [...currentUsers, {
        id: `usr-${Date.now()}`,
        name: normalizedName,
        status: 'Aktiv',
        hasOfficeAccess: formValues.hasOfficeAccess,
        hasProductionAccess: formValues.hasProductionAccess,
        isSupervisor: formValues.isSupervisor,
        supervisorCode: formValues.isSupervisor ? formValues.supervisorCode.trim().toUpperCase() : ''
      }]);
      setSuccessMessage(`${normalizedName} wurde angelegt.`);
    }
    setIsDialogOpen(false);
    window.setTimeout(() => setSuccessMessage(''), 4000);
  }
  return <section className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-1273">
      <header className="border-b border-border bg-surface px-5 py-5 sm:px-8 sm:py-6 lg:px-10" data-id="element-1274">
        <motion.div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: 'easeOut'
      }} data-id="element-1275">
          <div data-id="element-1276">
            <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-1277">
              Benutzerverwaltung
            </h1>
          </div>
          <Button onClick={openCreateDialog} leadingIcon={<PlusIcon size={18} aria-hidden="true" data-id="element-1279" />} data-id="element-1278">
            Benutzer anlegen
          </Button>
        </motion.div>
      </header>

      <div className="mx-auto w-full max-w-[1540px] px-5 py-6 sm:px-8 sm:py-8 lg:px-10" data-id="element-1280">
        <motion.div className="overflow-x-auto rounded-lg border border-border bg-surface shadow-sm" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} data-id="element-1281">
          <table className="w-full min-w-[940px] table-fixed text-left" data-id="element-1282">
            <caption className="sr-only" data-id="element-1283">
              Übersicht aller Benutzerkonten mit Status, Berechtigungen und
              Supervisor-Freigaben.
            </caption>
            <colgroup data-id="element-1284">
              <col className="w-[23%]" data-id="element-1285" />
              <col className="w-[14%]" data-id="element-1286" />
              <col className="w-[12%]" data-id="element-1287" />
              <col className="w-[14%]" data-id="element-1288" />
              <col className="w-[15%]" data-id="element-1289" />
              <col className="w-[14%]" data-id="element-1290" />
              <col className="w-[8%]" data-id="element-1291" />
            </colgroup>
            <thead className="border-b border-border bg-surface-subtle" data-id="element-1292">
              <tr className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-1293">
                <th scope="col" className="px-5 py-4" data-id="element-1294">
                  Benutzer
                </th>
                <th scope="col" className="px-4 py-4" data-id="element-1295">
                  Konto
                </th>
                <th scope="col" className="px-4 py-4" data-id="element-1296">
                  Office
                </th>
                <th scope="col" className="px-4 py-4" data-id="element-1297">
                  Produktion
                </th>
                <th scope="col" className="px-4 py-4" data-id="element-1298">
                  Rolle
                </th>
                <th scope="col" className="px-4 py-4" data-id="element-1299">
                  Supervisor-Code
                </th>
                <th scope="col" className="px-4 py-4 text-right" data-id="element-1300">
                  Aktion
                </th>
              </tr>
            </thead>
            <tbody aria-busy={isLoading} aria-label={isLoading ? 'Benutzerkonten werden geladen' : 'Benutzerkonten'} data-id="element-1301">
              {isLoading ? <UserManagementTableSkeleton data-id="element-1302" /> : users.map((user, index) => <motion.tr key={user.id} className="border-b border-border last:border-b-0 hover:bg-surface-subtle" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              delay: index * 0.045,
              ease: 'easeOut'
            }} data-id="element-1303">
                    <th scope="row" className="px-5 py-4 align-middle" data-id="element-1304">
                      <div className="flex min-w-0 items-center gap-3" data-id="element-1305">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-yellow-light text-text-primary" aria-hidden="true" data-id="element-1306">
                          <UserRoundIcon size={17} strokeWidth={1.9} data-id="element-1307" />
                        </span>
                        <span className="truncate font-heading text-sm text-text-primary" data-id="element-1308">
                          {user.name}
                        </span>
                      </div>
                    </th>
                    <td className="px-4 py-4 align-middle" data-id="element-1309">
                      <StatusBadge status={user.status} data-id="element-1310" />
                    </td>
                    <td className="px-4 py-4 align-middle" data-id="element-1311">
                      <PermissionBadge enabled={user.hasOfficeAccess} label="Office" data-id="element-1312" />
                    </td>
                    <td className="px-4 py-4 align-middle" data-id="element-1313">
                      <PermissionBadge enabled={user.hasProductionAccess} label="Produktion" data-id="element-1314" />
                    </td>
                    <td className="px-4 py-4 align-middle" data-id="element-1315">
                      {user.isSupervisor ? <span className="inline-flex items-center gap-1.5 rounded-md border border-brand-green bg-brand-yellow-light px-2.5 py-1.5 text-xs font-semibold text-text-primary" data-id="element-1316">
                          <ShieldCheckIcon size={14} aria-hidden="true" data-id="element-1317" />
                          Supervisor
                        </span> : <span className="text-sm text-text-secondary" data-id="element-1318">
                          Standard
                        </span>}
                    </td>
                    <td className="px-4 py-4 align-middle" data-id="element-1319">
                      {user.isSupervisor ? <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-subtle px-2.5 py-1.5 font-heading text-xs text-text-primary" aria-label="Supervisor-Code hinterlegt" data-id="element-1320">
                          <KeyRoundIcon size={13} aria-hidden="true" data-id="element-1321" />
                          •••••••
                        </span> : <span className="text-text-secondary" aria-label="Kein Supervisor-Code" data-id="element-1322">
                          —
                        </span>}
                    </td>
                    <td className="px-4 py-4 text-right align-middle" data-id="element-1323">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} leadingIcon={<PencilIcon size={17} aria-hidden="true" data-id="element-1325" />} aria-label={`${user.name} bearbeiten`} title={`${user.name} bearbeiten`} data-id="element-1324">
                        <span className="sr-only" data-id="element-1326">Bearbeiten</span>
                      </Button>
                    </td>
                  </motion.tr>)}
            </tbody>
          </table>
        </motion.div>
        <p className="mt-3 text-xs text-text-secondary sm:hidden" data-id="element-1327">
          Für weitere Spalten horizontal scrollen.
        </p>
      </div>

      <Modal open={isDialogOpen} onOpenChange={handleDialogOpenChange} title={dialogTitle} description={dialogDescription} size="md" closeLabel="Benutzerdialog schließen" footer={<>
            <Button type="button" variant="secondary" onClick={() => setIsDialogOpen(false)} data-id="element-1329">
              Abbrechen
            </Button>
            <Button type="submit" form="user-management-form" data-id="element-1330">
              {isEditing ? 'Änderungen speichern' : 'Benutzer erstellen'}
            </Button>
          </>} data-id="element-1328">
        <form id="user-management-form" noValidate onSubmit={handleSubmit} data-id="element-1331">
          <div className="space-y-5" data-id="element-1332">
            <TextInput id="user-name" name="name" label="Name" placeholder="Vorname Nachname" value={formValues.name} onChange={event => updateFormValue('name', event.target.value)} error={errors.name} required data-id="element-1333" />

            <TextInput id="user-password" name="password" type="password" autoComplete="new-password" label={isEditing ? 'Neues Passwort' : 'Passwort'} placeholder={isEditing ? 'Leer lassen, um das Passwort beizubehalten' : 'Sicheres Passwort eingeben'} hint={isEditing ? 'Optional. Nur ausfüllen, wenn das Passwort geändert werden soll.' : 'Mindestens 8 Zeichen verwenden.'} value={formValues.password} onChange={event => updateFormValue('password', event.target.value)} error={errors.password} required={!isEditing} data-id="element-1334" />

            <fieldset data-id="element-1335">
              <legend className="font-heading text-sm text-text-primary" data-id="element-1336">
                Berechtigungen <span aria-hidden="true" data-id="element-1337">*</span>
              </legend>
              <p className="mt-1 text-sm text-text-secondary" data-id="element-1338">
                Mindestens einen Arbeitsbereich auswählen.
              </p>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-6" data-id="element-1339">
                <Checkbox id="user-office-permission" name="officePermission" label="Office" checked={formValues.hasOfficeAccess} onCheckedChange={checked => updateFormValue('hasOfficeAccess', checked)} data-id="element-1340" />
                <Checkbox id="user-production-permission" name="productionPermission" label="Produktion" checked={formValues.hasProductionAccess} onCheckedChange={checked => updateFormValue('hasProductionAccess', checked)} data-id="element-1341" />
              </div>
              {errors.permissions ? <p className="mt-3 text-sm font-medium text-error" role="alert" data-id="element-1342">
                  {errors.permissions}
                </p> : null}
            </fieldset>

            <fieldset className="border-t border-border pt-5" data-id="element-1343">
              <Checkbox id="user-supervisor" name="supervisor" label="Supervisor" description="Ermöglicht Supervisor-Freigaben in der Produktion." checked={formValues.isSupervisor} onCheckedChange={checked => updateFormValue('isSupervisor', checked)} data-id="element-1344" />

              {formValues.isSupervisor ? <div className="mt-5" data-id="element-1345">
                  <TextInput id="user-supervisor-code" name="supervisorCode" label="Supervisor-Code" placeholder="z. B. SV-2847" value={formValues.supervisorCode} onChange={event => updateFormValue('supervisorCode', event.target.value)} error={errors.supervisorCode} hint="Der Code wird für Supervisor-Freigaben verwendet." required data-id="element-1346" />
                </div> : null}
            </fieldset>

            {editingUser ? <section className="border-t border-border pt-5" aria-labelledby="account-status-heading" data-id="element-1347">
                <h3 id="account-status-heading" className="font-heading text-sm text-text-primary" data-id="element-1348">
                  Kontostatus
                </h3>
                <p className="mt-1 text-sm text-text-secondary" data-id="element-1349">
                  {editingUser.status === 'Aktiv' ? 'Das Benutzerkonto kann sich aktuell anmelden.' : 'Das Benutzerkonto ist gesperrt und kann sich nicht anmelden.'}
                </p>
                <Button type="button" variant="secondary" size="sm" className="mt-4" onClick={() => toggleUserStatus(editingUser)} leadingIcon={editingUser.status === 'Aktiv' ? <LockKeyholeIcon size={15} aria-hidden="true" data-id="element-1351" /> : <LockKeyholeOpenIcon size={15} aria-hidden="true" data-id="element-1352" />} data-id="element-1350">
                  {editingUser.status === 'Aktiv' ? 'Benutzer sperren' : 'Benutzer entsperren'}
                </Button>
              </section> : null}
          </div>
        </form>
      </Modal>

      <AnimatePresence data-id="element-1353">
        {successMessage ? <motion.div role="status" aria-live="polite" className="fixed right-5 top-5 z-30 flex max-w-sm items-start gap-3 rounded-lg border border-success bg-surface p-4 shadow-md sm:right-8 sm:top-8" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} data-id="element-1354">
            <CheckCircle2Icon size={20} className="mt-0.5 shrink-0 text-success" aria-hidden="true" data-id="element-1355" />
            <div data-id="element-1356">
              <p className="font-heading text-sm text-text-primary" data-id="element-1357">
                Benutzerverwaltung aktualisiert
              </p>
              <p className="mt-1 text-xs leading-5 text-text-secondary" data-id="element-1358">
                {successMessage}
              </p>
            </div>
          </motion.div> : null}
      </AnimatePresence>
    </section>;
}
type StatusBadgeProps = {
  status: UserStatus;
};
function StatusBadge({
  status
}: StatusBadgeProps) {
  const isActive = status === 'Aktiv';
  return <span className={`inline-flex rounded-md px-2.5 py-1.5 text-xs font-semibold ${isActive ? 'bg-success/15 text-success' : 'border border-border bg-surface-subtle text-text-secondary'}`} data-id="element-1359">
      {status}
    </span>;
}
type PermissionBadgeProps = {
  enabled: boolean;
  label: string;
};
function PermissionBadge({
  enabled,
  label
}: PermissionBadgeProps) {
  return enabled ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-primary" data-id="element-1360">
      <CheckCircle2Icon size={16} className="text-success" aria-hidden="true" data-id="element-1361" />
      {label}
    </span> : <span className="text-sm text-text-secondary" data-id="element-1362">—</span>;
}
function UserManagementTableSkeleton() {
  return <>
      {Array.from({
      length: 4
    }, (_, index) => <tr key={index} className="animate-pulse border-b border-border last:border-b-0" aria-hidden="true" data-id="element-1363">
            <td className="px-5 py-4" data-id="element-1364">
              <div className="flex items-center gap-3" data-id="element-1365">
                <div className="h-9 w-9 shrink-0 rounded-full bg-border" data-id="element-1366" />
                <div className="h-4 w-28 rounded bg-border" data-id="element-1367" />
              </div>
            </td>
            <td className="px-4 py-4" data-id="element-1368">
              <div className="h-7 w-16 rounded-md bg-border" data-id="element-1369" />
            </td>
            <td className="px-4 py-4" data-id="element-1370">
              <div className="h-4 w-14 rounded bg-border" data-id="element-1371" />
            </td>
            <td className="px-4 py-4" data-id="element-1372">
              <div className="h-4 w-20 rounded bg-border" data-id="element-1373" />
            </td>
            <td className="px-4 py-4" data-id="element-1374">
              <div className="h-7 w-24 rounded-md bg-border" data-id="element-1375" />
            </td>
            <td className="px-4 py-4" data-id="element-1376">
              <div className="h-7 w-24 rounded-md bg-border" data-id="element-1377" />
            </td>
            <td className="px-4 py-4" data-id="element-1378">
              <div className="ml-auto h-8 w-8 rounded-md bg-border" data-id="element-1379" />
            </td>
          </tr>)}
    </>;
}
function validateUserForm(values: UserFormValues, isEditing: boolean): UserFormErrors {
  const errors: UserFormErrors = {};
  if (!values.name.trim()) {
    errors.name = 'Bitte geben Sie einen Namen ein.';
  }
  if (!isEditing && !values.password) {
    errors.password = 'Bitte legen Sie ein Passwort fest.';
  } else if (values.password && values.password.length < 8) {
    errors.password = 'Das Passwort muss mindestens 8 Zeichen enthalten.';
  }
  if (!values.hasOfficeAccess && !values.hasProductionAccess) {
    errors.permissions = 'Wählen Sie mindestens eine Berechtigung für Office oder Produktion.';
  }
  if (values.isSupervisor && !values.supervisorCode.trim()) {
    errors.supervisorCode = 'Für Supervisor ist ein Code erforderlich.';
  }
  return errors;
}